import { useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { callPostApi } from "../../function/ApiCallFunction";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import { CASHI_TAGLINE, cashiLogo } from "../../include/cashiBrand";

const emptyForm = {
  store_name: "",
  owner_name: "",
  contact_mobile: "",
  email: "",
  username: "",
  area_name: "",
  business_type_id: "",
  state_id: "",
  city_id: "",
  store_image: "",
  join_code: "",
};

const fieldSx = {
  "& .MuiInputLabel-root": { fontSize: 13, fontWeight: 600, color: "#111111", mb: 0.4, position: "static", transform: "none" },
  "& .MuiFormLabel-asterisk": { display: "none" },
  "& .MuiInputBase-root": { borderRadius: 2, background: "#fff", mt: 0.4 },
  "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
  "& .MuiInputBase-input": { fontSize: 15, py: 1.15 },
};

const steps = [
  { title: "Enter the store name, owner, and mobile" },
  { title: "Choose the business type, state, and city" },
  { title: "Optionally add a store photo" },
];

function StepIcon({ index }) {
  const common = { width: 18, height: 18, fill: "none", stroke: consoleColors.accent, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  if (index === 0) {
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <rect x="7" y="3" width="10" height="18" rx="2" />
        <path d="M11 18h2" />
      </svg>
    );
  }
  if (index === 1) {
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <path d="M4 19V8l8-4 8 4v11" />
        <path d="M9 19v-6h6v6" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" {...common}>
      <rect x="4" y="5" width="16" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.4" />
      <path d="M4 16l4.5-3.5L13 16l2-1.5L20 18" />
    </svg>
  );
}

export default function StoreRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [options, setOptions] = useState({ types: [], states: [], cities: [] });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    callPostApi("store-register", { mod: "STORE_REGISTER_OPTIONS", data_arr: {} }).then((result) => {
      if (!mounted || result.status !== "200") return;
      setOptions({ types: result.data?.types || [], states: result.data?.states || [], cities: result.data?.cities || [] });
    });
    return () => { mounted = false; };
  }, []);

  const citiesForState = options.cities.filter((city) => city.state_id === form.state_id);

  const onImage = (file) => {
    if (!file) return;
    if (file.size > 900000) {
      toast.info("Use a store image under 900 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((prev) => ({ ...prev, store_image: String(reader.result || "") }));
    reader.readAsDataURL(file);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.email || "").trim())) {
      toast.info("A valid email is required");
      return;
    }
    setSaving(true);
    try {
      const result = await callPostApi("store-register", { mod: "STORE_REGISTER", data_arr: form });
      if (result.status === "200") setDone(true);
      else toast.info(result.status_message || result.data?.error || "Could not register the store");
    } finally {
      setSaving(false);
    }
  };

  const span = { gridColumn: { xs: "auto", md: "1 / -1" } };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: { xs: "column", md: "row" }, background: consoleColors.page }}>
      <Box sx={{ display: { xs: "none", md: "flex" }, width: "34%", background: consoleColors.sidebar, color: "#fff", flexDirection: "column", justifyContent: "space-between", p: 6 }}>
        <Box>
          <Box component="img" src={cashiLogo} alt="Cashi" sx={{ height: 46, width: "auto" }} />
          <Typography sx={{ fontSize: 20, fontWeight: 650, lineHeight: 1.4, mt: 4, maxWidth: 360 }}>{CASHI_TAGLINE}</Typography>
          <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15, mt: 3, maxWidth: 360 }}>Open a store with Cashi</Typography>
          <Typography sx={{ color: consoleColors.navText, mt: 2, maxWidth: 340, fontSize: 15, lineHeight: 1.5 }}>
            Register your store. It is saved as pending, then an administrator activates it.
          </Typography>
          <Box sx={{ mt: 5, display: "grid", gap: 2 }}>
            {steps.map((step, index) => (
              <Box key={step.title} sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 2, background: "rgba(33,167,236,0.16)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <StepIcon index={index} />
                </Box>
                <Typography sx={{ color: consoleColors.navText, fontSize: 14 }}>{step.title}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Typography sx={{ color: consoleColors.navMuted, fontSize: 14 }}>Store sign up</Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 4 } }}>
        <Box sx={{ width: "100%", maxWidth: 820, background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 3, p: { xs: 2.2, sm: 3.2 } }}>
          {done ? (
            <Box>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: consoleColors.ink }}>Store saved</Typography>
              <Typography sx={{ color: consoleColors.muted, mt: 1.2, lineHeight: 1.55 }}>
                {form.store_name || "Your store"} is pending on the Lucky Draw Coupons plan. A thank-you email was sent to {form.email}. You cannot sign in until an administrator approves it. After approval, sign in with this email and a 6-digit code.
                {form.join_code ? ` Joining code ${form.join_code} will add this store to that club when it is approved.` : ""}
              </Typography>
              <Button onClick={() => navigate("/")} variant="contained" sx={{ ...primaryButtonSx, mt: 3 }}>Back to sign in</Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={submit}>
              <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
                <Box component="img" src={cashiLogo} alt="Cashi" sx={{ height: 32, width: "auto" }} />
                <Typography sx={{ fontSize: 14, fontWeight: 650, color: consoleColors.ink, mt: 1.2, lineHeight: 1.4 }}>{CASHI_TAGLINE}</Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: consoleColors.accent, fontWeight: 700, letterSpacing: 0.6 }}>STORE</Typography>
              <Typography sx={{ fontSize: { xs: 26, md: 30 }, fontWeight: 700, color: consoleColors.ink, mt: 0.4 }}>Create your store</Typography>
              <Typography sx={{ color: consoleColors.muted, fontSize: 14, mt: 0.6, mb: 2, lineHeight: 1.45 }}>
                No login needed. Your store is saved as pending until admin approval.
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.35 }}>
                <TextField required sx={fieldSx} label="Store name" placeholder="Your store name" value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} />
                <TextField required sx={fieldSx} label="Owner" placeholder="Owner full name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
                <TextField required sx={fieldSx} label="Mobile" placeholder="10-digit mobile" value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} inputProps={{ inputMode: "numeric" }} />
                <TextField required sx={fieldSx} label="Email" type="email" placeholder="Owner email for OTP login" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <TextField required sx={fieldSx} label="Username" placeholder="Store username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                <TextField sx={fieldSx} label="Area" placeholder="Area or locality" value={form.area_name} onChange={(e) => setForm({ ...form, area_name: e.target.value })} />
                <TextField required select sx={fieldSx} label="Business type" value={form.business_type_id} onChange={(e) => setForm({ ...form, business_type_id: e.target.value })}>
                  <MenuItem value="" disabled>Select business type</MenuItem>
                  {options.types.map((item) => <MenuItem key={item.type_id} value={item.type_id}>{item.name}</MenuItem>)}
                </TextField>
                <TextField required select sx={fieldSx} label="State" value={form.state_id} onChange={(e) => setForm({ ...form, state_id: e.target.value, city_id: "" })}>
                  <MenuItem value="" disabled>Select state</MenuItem>
                  {options.states.map((item) => <MenuItem key={item.state_id} value={item.state_id}>{item.name}</MenuItem>)}
                </TextField>
                <TextField required select sx={fieldSx} label="City" value={form.city_id} onChange={(e) => setForm({ ...form, city_id: e.target.value })} disabled={!form.state_id}>
                  <MenuItem value="" disabled>{form.state_id ? "Select city" : "Choose a state first"}</MenuItem>
                  {citiesForState.map((item) => <MenuItem key={item.city_id} value={item.city_id}>{item.name}</MenuItem>)}
                </TextField>
                <TextField sx={fieldSx} label="Plan" value="Lucky Draw Coupons" disabled />
                <TextField sx={{ ...fieldSx, ...span }} label="Joining code" placeholder="Club joining code, if you have one" value={form.join_code} onChange={(e) => setForm({ ...form, join_code: e.target.value.toUpperCase() })} />

                <Box sx={span}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: consoleColors.ink, mb: 0.6 }}>Store picture</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap", border: `1px solid ${consoleColors.line}`, borderRadius: 2, px: 1.2, py: 0.8 }}>
                    <Button component="label" sx={{ textTransform: "none", color: consoleColors.ink, border: `1px solid ${consoleColors.line}`, borderRadius: 2, px: 1.4 }}>
                      Choose file
                      <input hidden type="file" accept="image/*" onChange={(e) => onImage(e.target.files?.[0])} />
                    </Button>
                    <Typography sx={{ color: consoleColors.muted, fontSize: 13 }}>{form.store_image ? "Image selected" : "No file chosen"}</Typography>
                  </Box>
                  <Typography sx={{ color: consoleColors.muted, fontSize: 12, mt: 0.6 }}>Optional. JPEG, PNG or WebP, up to 900 KB.</Typography>
                  {form.store_image && <Box component="img" src={form.store_image} alt="Store" sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: 1.5, mt: 1 }} />}
                </Box>

                <Button type="submit" disabled={saving} variant="contained" sx={{ ...primaryButtonSx, ...span, minHeight: 48, fontSize: 16 }}>
                  {saving ? "Saving..." : "Save"}
                </Button>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                <Button type="button" onClick={() => navigate("/")} sx={{ mt: 1.2, textTransform: "none", color: consoleColors.muted }}>Already have a store? Sign in</Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
